/**
 * Timing constants for the tester session window (#4024).
 *
 * Job timing is described by two scalars:
 *   - `targetDurationMinutes` — base time the tester is expected to need.
 *   - `maxExtensionMinutes`   — total overtime budget on top of base.
 *
 * The absolute end of the window is `claimedAt + target + maxExtension`,
 * persisted as the stable `responseDeadline` column (#3997). A single
 * overtime-entry warning fires when the tester crosses
 * `claimedAt + target` (= `responseDeadline - maxExtension`).
 */

/** Default total overtime budget in minutes. */
export const DEFAULT_MAX_EXTENSION_MINUTES = 5;

/** Maximum allowed total session time in minutes (target + overtime budget). */
export const SYSTEM_MAX_SESSION_MINUTES = 120;

/** Setup buffer in minutes added to the hard cutoff for tester orientation. */
export const SETUP_BUFFER_MINUTES = 3;

/** Default target duration for a test job (minutes). */
export const DEFAULT_TARGET_DURATION_MINUTES = 15;

/**
 * Bounds for AI-driven per-job session-time estimation (#5120).
 *
 * When an AI writes a job's instructions (the `preparing` flow), an AI also
 * rightsizes the tester's session window by reading those instructions. The
 * estimate is bounded to `[floor, ceiling]`:
 *   - The CEILING is the most an unattended estimate may allot. A `preparing`
 *     job created without an explicit override seeds these values, so the
 *     funds hold reserved at creation already covers the worst case and the
 *     estimate (always ≤ ceiling) never needs re-reservation. An explicit
 *     caller duration overrides the ceiling — the estimate still can't exceed
 *     it, so a caller value caps rather than fixes the window.
 *   - The FLOOR is the least we'll allot, so a trivial-scope job is never
 *     rushed below a workable minimum. It is clamped to never exceed the
 *     ceiling (see {@link clampEstimatedSession}) so lower-budget paths can't
 *     invert the window.
 */
export const ESTIMATION_CEILING_TARGET_MINUTES = 30;
export const ESTIMATION_CEILING_EXTENSION_MINUTES = 10;
export const ESTIMATION_FLOOR_TARGET_MINUTES = 12;
export const ESTIMATION_FLOOR_EXTENSION_MINUTES = 5;

/** A target/overtime session-time pair, in minutes. */
export interface SessionMinutes {
  readonly targetDurationMinutes: number;
  readonly maxExtensionMinutes: number;
}

function clampToRange(value: number, floor: number, ceiling: number): number {
  return Math.max(floor, Math.min(value, ceiling));
}

/**
 * Clamp a raw AI duration estimate to `[floor, ceiling]`, where the ceiling is
 * the job's configured budget and the floor is the global minimum clamped to
 * never exceed that ceiling. Rounds to whole minutes. Throws on a non-finite
 * estimate — a malformed model output is a bug to surface, not a value to
 * paper over.
 */
export function clampEstimatedSession(
  estimate: SessionMinutes,
  ceilingTargetMinutes: number,
  ceilingExtensionMinutes: number,
): SessionMinutes {
  if (!Number.isFinite(estimate.targetDurationMinutes) || !Number.isFinite(estimate.maxExtensionMinutes)) {
    throw new Error(
      `Non-finite session-time estimate: target=${estimate.targetDurationMinutes}, extension=${estimate.maxExtensionMinutes}`,
    );
  }

  const targetFloor = Math.min(ESTIMATION_FLOOR_TARGET_MINUTES, ceilingTargetMinutes);
  const extensionFloor = Math.min(ESTIMATION_FLOOR_EXTENSION_MINUTES, ceilingExtensionMinutes);

  return {
    targetDurationMinutes: Math.round(clampToRange(estimate.targetDurationMinutes, targetFloor, ceilingTargetMinutes)),
    maxExtensionMinutes: Math.round(clampToRange(estimate.maxExtensionMinutes, extensionFloor, ceilingExtensionMinutes)),
  };
}

/**
 * Window during which a tester whose claim went `overdue` can still submit
 * late or drop the work. Matched to the tester JWT lifetime (`JWT_EXPIRY`
 * in `packages/api/src/utils/jwt.util.ts`) — once the JWT has expired the
 * tester cannot reach the late-submission UI anyway, so the job-monitor
 * lapses the row to `abandoned/tester_dropped` at the same threshold.
 *
 * Computed from `claimedAt`, not from the moment of transition to
 * `overdue` — the tester JWT is signed at claim time, so the wall clock
 * for usable-token-remaining is anchored there.
 */
export const LATE_SUBMISSION_WINDOW_HOURS = 24;

/**
 * Compute the wall-clock deadline at which an `overdue` job auto-lapses
 * to `abandoned/tester_dropped`. Returns an ISO string for direct
 * persistence on `TestJob.overdueLapsesAt`.
 */
export function computeOverdueLapsesAt(claimedAt: string): string {
  return new Date(new Date(claimedAt).getTime() + LATE_SUBMISSION_WINDOW_HOURS * 60 * 60 * 1000).toISOString();
}

/**
 * Total allotted time in minutes — base duration plus the full overtime
 * budget. Used for late-submission billing, which charges for the full
 * window the tester held the claim.
 */
export function computeAllottedMinutes(
  targetDurationMinutes: number,
  maxExtensionMinutes: number,
): number {
  return targetDurationMinutes + maxExtensionMinutes;
}

/**
 * Clamp a raw test duration to the per-job allotted window. The cap is
 * derived from the job's own `targetDurationMinutes + maxExtensionMinutes`
 * (see #4602) — so a 10-minute job caps at 10 minutes of duration, a
 * 90-minute job caps at 90, etc. Used by every write path that persists
 * `testDurationSeconds`, and as defense-in-depth on the read side for
 * aggregated stats.
 *
 * Matches the semantic already enforced by the `overdue → completed`
 * billing branch in `JobManager.updateJobStatus`, which bills the
 * allotted window regardless of wall-clock elapsed time.
 */
export function clampDurationSecondsToAllotted(
  durationSeconds: number,
  targetDurationMinutes: number,
  maxExtensionMinutes: number,
): number {
  const capSeconds = computeAllottedMinutes(targetDurationMinutes, maxExtensionMinutes) * 60;
  return Math.min(durationSeconds, capSeconds);
}

/**
 * Grace added to the stable `responseDeadline` before a `working` job is
 * considered past its hard deadline (#3997). Lets a tester finish up after
 * the deadline lands before the monitor flips them to `overdue`.
 */
export const HARD_TIMEOUT_GRACE_MINUTES = 2;

/**
 * Grace used by the `claimedAt`-anchored fallback when `responseDeadline`
 * is absent (e.g. a mobile claim bug, #2363). Wider than
 * {@link HARD_TIMEOUT_GRACE_MINUTES} because the reconstructed deadline is
 * itself approximate.
 */
export const FALLBACK_TIMEOUT_GRACE_MINUTES = 5;

/** The timing fields {@link computeWorkingTimeoutCutoff} reads off a job. */
export interface WorkingTimeoutInputs {
  readonly responseDeadline?: string | null;
  readonly claimedAt?: string | null;
  readonly targetDurationMinutes: number;
  readonly maxExtensionMinutes: number;
}

/**
 * Wall-clock moment a `working` job is considered past its hard deadline —
 * the threshold the monitor uses to flip it to `overdue`, and the same
 * threshold admin-unhold uses to decide whether a job recovered without an
 * activity-log prior-status row should return to `working` or `overdue`
 * (#5748).
 *
 * Prefers the stable `responseDeadline` (+{@link HARD_TIMEOUT_GRACE_MINUTES}).
 * Falls back to reconstructing from the claim anchor
 * (`claimedAt + target + maxExtension + {@link FALLBACK_TIMEOUT_GRACE_MINUTES}`)
 * when the deadline column is absent. Returns null when neither input set is
 * available — the caller has no basis to judge the deadline and must not.
 */
export function computeWorkingTimeoutCutoff(job: WorkingTimeoutInputs): Date | null {
  if (job.responseDeadline) {
    return new Date(new Date(job.responseDeadline).getTime() + HARD_TIMEOUT_GRACE_MINUTES * 60 * 1000);
  }

  if (job.claimedAt && job.targetDurationMinutes) {
    const totalMinutes = job.targetDurationMinutes + job.maxExtensionMinutes + FALLBACK_TIMEOUT_GRACE_MINUTES;
    return new Date(new Date(job.claimedAt).getTime() + totalMinutes * 60 * 1000);
  }

  return null;
}

/**
 * Absolute hard cutoff time for a session — the moment recording must
 * stop regardless of tester action. Adds a setup buffer on top of the
 * full window so tester orientation doesn't eat into testing time.
 */
export function computeHardCutoffTime(
  claimedAt: string,
  targetDurationMinutes: number,
  maxExtensionMinutes: number,
): Date {
  const totalMinutes = SETUP_BUFFER_MINUTES + targetDurationMinutes + maxExtensionMinutes;
  return new Date(new Date(claimedAt).getTime() + totalMinutes * 60 * 1000);
}

/** Validation result for session-time configuration. */
export interface ExtensionValidationError {
  readonly field: 'systemLimit';
  readonly message: string;
}

/**
 * Validate session-time configuration. Returns an array of errors
 * (empty = valid).
 */
export function validateExtensionConfig(
  targetDurationMinutes: number,
  maxExtensionMinutes: number,
): readonly ExtensionValidationError[] {
  const errors: ExtensionValidationError[] = [];

  if (targetDurationMinutes + maxExtensionMinutes > SYSTEM_MAX_SESSION_MINUTES) {
    errors.push({
      field: 'systemLimit',
      message: `Total session time cannot exceed ${SYSTEM_MAX_SESSION_MINUTES} minutes (${targetDurationMinutes} + ${maxExtensionMinutes} = ${targetDurationMinutes + maxExtensionMinutes})`,
    });
  }

  return errors;
}
